import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
} from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useUserContext } from "@/contexts/UserContext/index.context";
import { extractOrganisationSettings } from "@/utils/constants/organizations";
import ComponentContainer from "../../shared/components/ComponentContainer";
import type { DeliveryFormType, VehiclePrefix } from "../types";

interface Props {
  form: DeliveryFormType;
  fieldPrefix: VehiclePrefix;
  title: string;
}

export function VehicleSection({ form, fieldPrefix, title }: Props) {
  const { t } = useTranslation();
  const { userState } = useUserContext();
  const { settings } = userState ? extractOrganisationSettings(userState) : { settings: undefined };
  const carDefaultValues = settings?.defaultValues?.cars;

  const [chosenModel, setChosenModel] = useState<string>(carDefaultValues?.[0]?.model ?? "");

  const motorizationOptions = [
    { name: t("delivery.form.vehicle.motorization.gas"), value: "GAS" },
    { name: t("delivery.form.vehicle.motorization.hybrid"), value: "HYBRID" },
    {
      name: t("delivery.form.vehicle.motorization.electric"),
      value: "ELECTRIC",
    },
  ];

  return (
    <ComponentContainer title={title}>
      <Grid container spacing={6}>
        {/* Category select */}
        <Grid
          size={{
            xs: carDefaultValues ? 6 : 12,
            lg: carDefaultValues ? 3 : 6,
          }}
        >
          <form.AppField name={`${fieldPrefix}.carType`}>
            {(field) => (
              <field.SelectField
                label={t("delivery.form.vehicle.category.title")}
                options={[
                  {
                    name: t("delivery.form.vehicle.category.a"),
                    value: "A",
                  },
                  {
                    name: t("delivery.form.vehicle.category.b"),
                    value: "B",
                  },
                  {
                    name: t("delivery.form.vehicle.category.c"),
                    value: "C",
                  },
                ]}
              />
            )}
          </form.AppField>
        </Grid>

        {/* Model dropdown (org-specific defaults) */}
        {carDefaultValues && (
          <Grid size={{ xs: 6, lg: 3 }}>
            <FormControl variant="standard" fullWidth>
              <InputLabel id={`${fieldPrefix}-model-select`} sx={{ textTransform: "capitalize" }}>
                {t("delivery.form.vehicle.model")}
              </InputLabel>
              <Select
                labelId={`${fieldPrefix}-model-select`}
                value={chosenModel}
                onChange={(e) => {
                  const val = e.target.value;
                  setChosenModel(val);
                  if (!carDefaultValues) return;
                  const selectedCar = carDefaultValues.find((car) => car.model === val);
                  if (selectedCar) {
                    form.setFieldValue(`${fieldPrefix}.marque`, selectedCar.marque ?? "");
                    form.setFieldValue(`${fieldPrefix}.model`, selectedCar.model ?? "");
                    form.setFieldValue(`${fieldPrefix}.carType`, selectedCar.carType ?? "");
                    form.setFieldValue(`${fieldPrefix}.type`, selectedCar.type ?? "");
                  }
                }}
              >
                {carDefaultValues.map((car, idx) => (
                  <MenuItem key={`${idx}-${car.model}`} value={car.model}>
                    Model: {car.model}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {/* Motorization radio */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <form.AppField name={`${fieldPrefix}.type`}>
            {(field) => {
              // Set default to first option if empty
              if (!field.state.value && motorizationOptions[0]) {
                field.handleChange(motorizationOptions[0].value);
              }

              return (
                <FormControl fullWidth>
                  <FormLabel
                    id={`${fieldPrefix}-motorization`}
                    sx={{ textTransform: "capitalize" }}
                  >
                    {t("delivery.form.vehicle.motorization.title")}
                  </FormLabel>
                  <RadioGroup
                    row
                    value={field.state.value || ""}
                    onChange={(e) => field.handleChange(e.target.value)}
                    aria-labelledby={`${fieldPrefix}-motorization`}
                  >
                    <Stack direction="row" width="80%" justifyContent="space-between">
                      {motorizationOptions.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          value={option.value}
                          control={<Radio />}
                          label={option.name}
                          sx={{ textTransform: "capitalize" }}
                        />
                      ))}
                    </Stack>
                  </RadioGroup>
                </FormControl>
              );
            }}
          </form.AppField>
        </Grid>
      </Grid>

      {/* Detail fields */}
      <Grid container spacing={6} sx={{ mt: 1 }}>
        <Grid size={{ xs: 12, lg: 3 }}>
          <form.AppField name={`${fieldPrefix}.vin`}>
            {(field) => <field.TextField label="VIN" />}
          </form.AppField>
        </Grid>
        <Grid size={{ xs: 12, lg: 3 }}>
          <form.AppField name={`${fieldPrefix}.vehicleCode`}>
            {(field) => <field.TextField label={t("delivery.form.vehicle.licence-plate")} />}
          </form.AppField>
        </Grid>
        <Grid size={{ xs: 12, lg: 3 }}>
          <form.AppField name={`${fieldPrefix}.marque`}>
            {(field) => <field.TextField label={t("delivery.form.vehicle.brand")} />}
          </form.AppField>
        </Grid>
        <Grid size={{ xs: 12, lg: 3 }}>
          <form.AppField name={`${fieldPrefix}.model`}>
            {(field) => <field.TextField label={t("delivery.form.vehicle.model")} />}
          </form.AppField>
        </Grid>
      </Grid>
    </ComponentContainer>
  );
}
